import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { useSetAtom } from 'jotai';

import { QaIdentifier, useMultipleQa } from 'hooks/useQa';

import { filterReset } from 'data/Atom/Component';
import { filterStore } from 'data/Atom/Store';

import { useExpandableList } from './__hooks/useExpandableList';
import { useFilter } from './__hooks/useFilter';
import GroupHeadline, { GroupHeadlineProps } from './__partials/GroupHeadline';
import { config } from './config';
import { FilterElement } from './types';

import styles from './Segment.module.scss';

interface SegmentProps {
    filterList: FilterElement[];
    headline?: GroupHeadlineProps;
    withDeleteLink?: boolean;
    onDelete?: () => void;
    overflowThreshold?: number;
    qaIdentifier?: QaIdentifier;
}

const Segment = ({
    filterList,
    headline,
    withDeleteLink = false,
    onDelete,
    overflowThreshold = config.overflowThreshold.default,
    qaIdentifier,
}: SegmentProps) => {
    const { t } = useTranslation();
    const [qaAttr, qaAttrReset, qaAttrShowAll] = useMultipleQa([
        qaIdentifier,
        QaIdentifier.Search_Filter_Section_Reset,
        QaIdentifier.Search_Filter_Section_ShowAll,
    ]);

    const filteredElements = useFilter({ filterList, headline });
    const { showAll, setShowAll, visibleFilters, hasOverflow } =
        useExpandableList(filteredElements, overflowThreshold);
    const setIsFilterReset = useSetAtom(filterReset);

    useEffect(() => {
        filterStore.sub(filterReset, () => {
            if (filterStore.get(filterReset)) {
                setShowAll(false);
            }
        });
    }, [setIsFilterReset, setShowAll]);

    if (!filteredElements.length) {
        return null;
    }
    const showDelete =
        withDeleteLink && filterList.some((element) => element.isSelected);

    return (
        <section {...qaAttr}>
            {headline && (
                <GroupHeadline {...headline}>
                    {showDelete && (
                        <span
                            className={styles.Delete}
                            onClick={() => {
                                onDelete?.();
                            }}
                            {...qaAttrReset}
                        >
                            {t('TRANS-delete')}
                        </span>
                    )}
                </GroupHeadline>
            )}

            {visibleFilters.map((element) => element.content)}

            {hasOverflow && (
                <span
                    className={styles.Toggle}
                    onClick={() => setShowAll((visible) => !visible)}
                    {...qaAttrShowAll}
                >
                    {showAll
                        ? t('TRANS-show-less')
                        : t('TRANS-show-all-with-count', {
                              count: filteredElements.length,
                          })}
                </span>
            )}
        </section>
    );
};

export default Segment;
